import { MaintenanceEntry, Truck, UserRole } from '../types';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getDistanceUnitLabel, getSavedDistanceUnit, KM_PER_MILE } from '../utils/distance';

export type FieldRole = 'Driver' | 'Mechanic';
export type Severity = 'low' | 'medium' | 'high' | 'critical';
export type WorkOrderStatus = 'draft' | 'awaiting-approval' | 'approved' | 'in-progress' | 'completed' | 'invoiced';
export type IntegrationProvider = 'Samsara' | 'Motive' | 'Geotab' | 'GPS/ELD CSV';
export type ComplianceDocumentType = 'DVIR' | 'DOT Inspection' | 'Insurance' | 'Registration' | 'IFTA' | 'Permit' | 'Driver File';

export interface FleetInspection {
  id: string;
  truckId: string;
  truckLabel: string;
  inspectorName: string;
  inspectorRole: FieldRole;
  odometer: number;
  checklist: Record<string, boolean>;
  notes: string;
  signatureName: string;
  photoDataUrl?: string;
  issueCount: number;
  status: 'passed' | 'attention-needed' | 'repair-required';
  synced: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface IssueReport {
  id: string;
  truckId: string;
  truckLabel: string;
  reportedBy: string;
  severity: Severity;
  category: 'Safety' | 'Mechanical' | 'Tires' | 'Brakes' | 'Electrical' | 'Body' | 'Other';
  description: string;
  photoDataUrl?: string;
  status: 'open' | 'triaged' | 'converted-to-work-order' | 'resolved';
  createdAt: Date;
  updatedAt: Date;
}

export interface WorkOrderPartReservation {
  partId: string;
  partName: string;
  quantity: number;
  unitCost: number;
}

export interface WorkOrder {
  id: string;
  truckId: string;
  truckLabel: string;
  title: string;
  type: 'preventive' | 'repair' | 'inspection' | 'recall' | 'diagnostic';
  priority: Severity;
  status: WorkOrderStatus;
  assignedTo: string;
  requestedBy: string;
  scheduledDate: Date;
  laborHours: number;
  laborRate: number;
  parts: WorkOrderPartReservation[];
  approvalRequired: boolean;
  approvedBy?: string;
  approvedAt?: Date;
  invoiceNumber?: string;
  invoiceStatus: 'not-created' | 'draft' | 'sent' | 'paid';
  notes: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface IntegrationConnector {
  id: IntegrationProvider;
  name: IntegrationProvider;
  status: 'not-connected' | 'connected' | 'needs-attention';
  authMode: 'api-key' | 'oauth' | 'file-import';
  connectionLabel?: string;
  lastSync?: Date;
  syncHealth: number;
  recordsSynced: number;
  notes: string;
}

export interface TelematicsEvent {
  id: string;
  provider: IntegrationProvider;
  truckId: string;
  truckLabel: string;
  recordedAt: Date;
  odometer?: number;
  fuelPercent?: number;
  fuelGallons?: number;
  miles?: number;
  faultCode?: string;
  severity: Severity;
  description: string;
}

export interface ComplianceDocument {
  id: string;
  truckId: string;
  truckLabel: string;
  type: ComplianceDocumentType;
  documentNumber: string;
  issuer: string;
  expirationDate: Date;
  owner: string;
  status: 'valid' | 'due-soon' | 'expired' | 'missing';
  attachmentName?: string;
  attachmentDataUrl?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: UserRole | 'driver' | 'mechanic' | 'dispatcher';
  status: 'active' | 'pending' | 'invited' | 'disabled';
  inviteCode: string;
  inviteLink: string;
  inviteSentAt: Date;
  inviteExpiresAt: Date;
  acceptedAt?: Date;
  lastActive?: Date;
  permissions: string[];
  createdAt: Date;
  updatedAt: Date;
}

export interface TeamInviteIndexEntry {
  code: string;
  workspaceUserId: string;
  memberId: string;
  companyId: string;
  email: string;
  role: TeamMember['role'];
  permissions: string[];
  status: TeamMember['status'] | 'revoked' | 'expired';
  expiresAt: string;
  createdAt: string;
  acceptedAt?: string;
}

interface TeamInviteTokenPayload {
  code: string;
  workspaceUserId: string;
  memberId: string;
  email: string;
  role: TeamMember['role'];
  permissions: string[];
  status: TeamInviteIndexEntry['status'];
  expiresAt: string;
  createdAt: string;
}

export interface FleetAuditEvent {
  id: string;
  action: string;
  actor: string;
  entity: string;
  detail: string;
  createdAt: Date;
}

export interface FleetOpsWorkspace {
  inspections: FleetInspection[];
  issueReports: IssueReport[];
  workOrders: WorkOrder[];
  connectors: IntegrationConnector[];
  telematicsEvents: TelematicsEvent[];
  complianceDocuments: ComplianceDocument[];
  teamMembers: TeamMember[];
  auditEvents: FleetAuditEvent[];
  offlineQueue: Array<{ id: string; type: string; label: string; createdAt: Date }>;
}

export interface PredictiveMaintenanceScore {
  truckId: string;
  truckLabel: string;
  riskScore: number;
  riskLevel: 'low' | 'moderate' | 'high' | 'critical';
  drivers: string[];
  recommendedActions: string[];
  nextServiceWindow: string;
}

export interface FleetReportMetrics {
  costPerMile: number;
  totalMaintenanceCost: number;
  downtimeHours: number;
  pmCompliance: number;
  utilization: number;
  fuelGallons: number;
  fuelCostEstimate: number;
  openWorkOrders: number;
  overdueCompliance: number;
  criticalFaults: number;
  monthlyCosts: Array<{ month: string; cost: number }>;
}

const STORAGE_PREFIX = 'fleetfix_ops_workspace_';
const TEAM_INVITE_INDEX_KEY = 'fleetfix_team_invites_index';
const FUEL_COST_PER_GALLON = 4.25;
const INVITE_LIFETIME_DAYS = 14;
const PUBLIC_APP_URL = process.env.REACT_APP_PUBLIC_APP_URL || 'https://fleet-fix-alpha.vercel.app';

const toDate = (value: unknown): Date => value instanceof Date ? value : new Date(String(value));
const isSeedRecord = (item: { id?: string }): boolean => Boolean(item.id?.includes('-demo-'));

const createId = (prefix: string): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

const getTruckLabel = (truck?: Truck): string => {
  if (!truck) return 'Unassigned vehicle';
  return `${truck.nickname || truck.licensePlate} · ${truck.year} ${truck.make} ${truck.model}`;
};

class FleetOpsService {
  public getWorkspace(userId: string): FleetOpsWorkspace {
    const saved = localStorage.getItem(this.getStorageKey(userId));
    if (!saved) {
      const seeded = this.createSeedWorkspace();
      this.saveWorkspace(userId, seeded);
      return seeded;
    }

    try {
      return this.reviveWorkspace(JSON.parse(saved), userId);
    } catch (error) {
      console.error('Error loading FleetOps workspace:', error);
      const seeded = this.createSeedWorkspace();
      this.saveWorkspace(userId, seeded);
      return seeded;
    }
  }

  public saveWorkspace(userId: string, workspace: FleetOpsWorkspace): void {
    localStorage.setItem(this.getStorageKey(userId), JSON.stringify(workspace));
  }

  public async publishTeamInvite(userId: string, member: TeamMember, companyName = 'FleetFix Team Workspace'): Promise<void> {
    try {
      await setDoc(doc(db, 'teamInvites', member.inviteCode), {
        code: member.inviteCode,
        workspaceUserId: userId,
        memberId: member.id,
        companyId: userId,
        companyName,
        email: member.email.toLowerCase(),
        name: member.name,
        role: member.role,
        permissions: member.permissions,
        status: member.status,
        inviteLink: member.inviteLink,
        expiresAt: member.inviteExpiresAt.toISOString(),
        createdAt: member.inviteSentAt.toISOString(),
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.warn('Unable to publish team invite status:', error);
    }
  }

  public async acceptRemoteTeamInvite(invite: TeamInviteIndexEntry, actor: string): Promise<void> {
    this.savePendingTeamClaim(invite);
    this.markInviteAccepted(invite.workspaceUserId, invite.memberId, actor);

    try {
      await setDoc(doc(db, 'teamInvites', invite.code), {
        ...invite,
        status: 'active',
        acceptedAt: new Date().toISOString(),
        acceptedBy: actor,
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.warn('Unable to sync accepted team invite:', error);
    }
  }

  public async syncRemoteTeamInviteStatuses(userId: string): Promise<FleetOpsWorkspace> {
    const workspace = this.getWorkspace(userId);
    let changed = false;
    const nextMembers = await Promise.all(workspace.teamMembers.map(async member => {
      try {
        const inviteDoc = await getDoc(doc(db, 'teamInvites', member.inviteCode));
        if (!inviteDoc.exists()) return member;
        const remote = inviteDoc.data() as Partial<TeamInviteIndexEntry> & { acceptedAt?: string };
        if (remote.status === 'active' && member.status !== 'active') {
          changed = true;
          return {
            ...member,
            status: 'active' as const,
            acceptedAt: remote.acceptedAt ? toDate(remote.acceptedAt) : new Date(),
            lastActive: new Date(),
            updatedAt: new Date()
          };
        }
      } catch (error) {
        console.warn('Unable to sync invite status:', error);
      }
      return member;
    }));

    if (!changed) return workspace;

    const updated = {
      ...workspace,
      teamMembers: nextMembers,
      auditEvents: [
        this.audit('FleetFix', 'Synced invite acceptance', 'Company account', 'Remote invite acceptance status refreshed.'),
        ...workspace.auditEvents
      ]
    };
    this.saveWorkspace(userId, updated);
    nextMembers.forEach(member => this.upsertInviteIndex(userId, member, member.status));
    return updated;
  }

  public createInspection(userId: string, inspection: Omit<FleetInspection, 'id' | 'createdAt' | 'updatedAt'>, actor: string): FleetOpsWorkspace {
    return this.updateWorkspace(userId, workspace => {
      const newInspection: FleetInspection = {
        ...inspection,
        id: createId('inspection'),
        createdAt: new Date(),
        updatedAt: new Date()
      };

      workspace.inspections = [newInspection, ...workspace.inspections];
      if (!newInspection.synced) {
        workspace.offlineQueue = [
          { id: newInspection.id, type: 'inspection', label: `${newInspection.truckLabel} inspection`, createdAt: new Date() },
          ...workspace.offlineQueue
        ];
      }

      workspace.auditEvents = [
        this.audit(actor, 'Created inspection', 'Field workflow', `${newInspection.status} inspection for ${newInspection.truckLabel}`),
        ...workspace.auditEvents
      ];

      return workspace;
    });
  }

  public deleteInspection(userId: string, inspectionId: string, actor: string): FleetOpsWorkspace {
    return this.updateWorkspace(userId, workspace => {
      const existing = workspace.inspections.find(inspection => inspection.id === inspectionId);
      if (!existing) return workspace;

      workspace.inspections = workspace.inspections.filter(inspection => inspection.id !== inspectionId);
      workspace.offlineQueue = workspace.offlineQueue.filter(item => item.id !== inspectionId);
      workspace.auditEvents = [
        this.audit(actor, 'Deleted inspection', 'Field workflow', `${existing.truckLabel} inspection record was removed`),
        ...workspace.auditEvents
      ];

      return workspace;
    });
  }

  public createIssueReport(userId: string, issue: Omit<IssueReport, 'id' | 'status' | 'createdAt' | 'updatedAt'>, actor: string): FleetOpsWorkspace {
    return this.updateWorkspace(userId, workspace => {
      const newIssue: IssueReport = {
        ...issue,
        id: createId('issue'),
        status: 'open',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      workspace.issueReports = [newIssue, ...workspace.issueReports];
      workspace.auditEvents = [
        this.audit(actor, 'Logged issue', 'Issue report', `${newIssue.severity} ${newIssue.category.toLowerCase()} issue on ${newIssue.truckLabel}`),
        ...workspace.auditEvents
      ];

      return workspace;
    });
  }

  public deleteIssueReport(userId: string, issueId: string, actor: string): FleetOpsWorkspace {
    return this.updateWorkspace(userId, workspace => {
      const existing = workspace.issueReports.find(issue => issue.id === issueId);
      if (!existing) return workspace;

      workspace.issueReports = workspace.issueReports.filter(issue => issue.id !== issueId);
      workspace.auditEvents = [
        this.audit(actor, 'Deleted issue report', 'Issue report', `${existing.category} issue on ${existing.truckLabel} was removed`),
        ...workspace.auditEvents
      ];

      return workspace;
    });
  }

  public updateIssueReport(userId: string, issueId: string, updates: Partial<IssueReport>, actor: string): FleetOpsWorkspace {
    return this.updateWorkspace(userId, workspace => {
      const existing = workspace.issueReports.find(issue => issue.id === issueId);
      if (!existing) return workspace;

      workspace.issueReports = workspace.issueReports.map(issue =>
        issue.id === issueId ? { ...issue, ...updates, updatedAt: new Date() } : issue
      );

      const updated = workspace.issueReports.find(issue => issue.id === issueId);
      workspace.auditEvents = [
        this.audit(actor, 'Updated issue report', 'Issue report', `${updated?.truckLabel || issueId} moved to ${updates.status || 'updated'}`),
        ...workspace.auditEvents
      ];

      return workspace;
    });
  }

  public convertIssueToWorkOrder(userId: string, issueId: string, actor: string): FleetOpsWorkspace {
    return this.updateWorkspace(userId, workspace => {
      const issue = workspace.issueReports.find(item => item.id === issueId);
      if (!issue || issue.status === 'converted-to-work-order' || issue.status === 'resolved') return workspace;

      const priority: Severity = issue.severity === 'critical' ? 'critical' : issue.severity === 'high' ? 'high' : 'medium';
      const workOrder: WorkOrder = {
        id: createId('wo'),
        truckId: issue.truckId,
        truckLabel: issue.truckLabel,
        title: `${issue.category} issue: ${issue.description.slice(0, 52)}${issue.description.length > 52 ? '...' : ''}`,
        type: issue.category === 'Safety' ? 'inspection' : 'repair',
        priority,
        status: priority === 'critical' ? 'approved' : 'awaiting-approval',
        assignedTo: priority === 'critical' ? 'Internal shop' : 'Unassigned',
        requestedBy: actor,
        scheduledDate: new Date(Date.now() + (priority === 'critical' ? 0 : 86400000)),
        laborHours: priority === 'critical' ? 3 : 1.5,
        laborRate: 95,
        parts: [],
        approvalRequired: priority !== 'critical',
        invoiceStatus: 'not-created',
        notes: issue.description,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      workspace.workOrders = [workOrder, ...workspace.workOrders];
      workspace.issueReports = workspace.issueReports.map(item =>
        item.id === issueId ? { ...item, status: 'converted-to-work-order', updatedAt: new Date() } : item
      );
      workspace.auditEvents = [
        this.audit(actor, 'Converted issue to work order', 'Issue report', `${issue.truckLabel} issue became ${workOrder.title}`),
        ...workspace.auditEvents
      ];

      return workspace;
    });
  }

  public createWorkOrder(userId: string, workOrder: Omit<WorkOrder, 'id' | 'createdAt' | 'updatedAt' | 'approvedAt'>, actor: string): FleetOpsWorkspace {
    return this.updateWorkspace(userId, workspace => {
      const newWorkOrder: WorkOrder = {
        ...workOrder,
        id: createId('wo'),
        createdAt: new Date(),
        updatedAt: new Date()
      };

      workspace.workOrders = [newWorkOrder, ...workspace.workOrders];
      workspace.auditEvents = [
        this.audit(actor, 'Created work order', 'Work order', `${newWorkOrder.title} for ${newWorkOrder.truckLabel}`),
        ...workspace.auditEvents
      ];

      return workspace;
    });
  }

  public updateWorkOrder(userId: string, workOrderId: string, updates: Partial<WorkOrder>, actor: string): FleetOpsWorkspace {
    return this.updateWorkspace(userId, workspace => {
      workspace.workOrders = workspace.workOrders.map(workOrder => {
        if (workOrder.id !== workOrderId) return workOrder;
        return { ...workOrder, ...updates, updatedAt: new Date() };
      });

      const updated = workspace.workOrders.find(workOrder => workOrder.id === workOrderId);
      workspace.auditEvents = [
        this.audit(actor, 'Updated work order', 'Work order', `${updated?.title || workOrderId} moved to ${updates.status || 'updated'}`),
        ...workspace.auditEvents
      ];

      return workspace;
    });
  }

  public createComplianceDocument(userId: string, document: Omit<ComplianceDocument, 'id' | 'status' | 'createdAt' | 'updatedAt'>, actor: string): FleetOpsWorkspace {
    return this.updateWorkspace(userId, workspace => {
      const newDocument: ComplianceDocument = {
        ...document,
        id: createId('doc'),
        status: this.getComplianceStatus(document.expirationDate),
        createdAt: new Date(),
        updatedAt: new Date()
      };

      workspace.complianceDocuments = [newDocument, ...workspace.complianceDocuments];
      workspace.auditEvents = [
        this.audit(actor, 'Added compliance document', 'Compliance', `${newDocument.type} for ${newDocument.truckLabel}`),
        ...workspace.auditEvents
      ];

      return workspace;
    });
  }

  public updateComplianceDocument(userId: string, documentId: string, updates: Partial<ComplianceDocument>, actor: string): FleetOpsWorkspace {
    return this.updateWorkspace(userId, workspace => {
      const existing = workspace.complianceDocuments.find(document => document.id === documentId);
      if (!existing) return workspace;

      workspace.complianceDocuments = workspace.complianceDocuments.map(document => {
        if (document.id !== documentId) return document;

        const expirationDate = updates.expirationDate ? toDate(updates.expirationDate) : document.expirationDate;
        return {
          ...document,
          ...updates,
          expirationDate,
          status: updates.status || this.getComplianceStatus(expirationDate),
          updatedAt: new Date()
        };
      });

      const updated = workspace.complianceDocuments.find(document => document.id === documentId);
      workspace.auditEvents = [
        this.audit(actor, 'Updated compliance record', 'Compliance', `${updated?.type || documentId} for ${updated?.truckLabel || 'fleet'} reviewed`),
        ...workspace.auditEvents
      ];

      return workspace;
    });
  }

  public updateConnector(userId: string, connectorId: IntegrationProvider, updates: Partial<IntegrationConnector>, actor: string): FleetOpsWorkspace {
    return this.updateWorkspace(userId, workspace => {
      workspace.connectors = workspace.connectors.map(connector =>
        connector.id === connectorId ? { ...connector, ...updates } : connector
      );

      workspace.auditEvents = [
        this.audit(actor, 'Updated integration', 'Telematics', `${connectorId} is ${updates.status || 'updated'}`),
        ...workspace.auditEvents
      ];

      return workspace;
    });
  }

  public runTelematicsSync(userId: string, connectorId: IntegrationProvider, trucks: Truck[], actor: string): FleetOpsWorkspace {
    return this.updateWorkspace(userId, workspace => {
      const connector = workspace.connectors.find(item => item.id === connectorId);
      if (connectorId !== 'GPS/ELD CSV' && connector?.status !== 'connected') {
        workspace.connectors = workspace.connectors.map(item =>
          item.id === connectorId ? { ...item, status: 'needs-attention', syncHealth: 0 } : item
        );
        workspace.auditEvents = [
          this.audit(actor, 'Blocked telematics sync', 'Telematics', `${connectorId} needs a connected credential before syncing`),
          ...workspace.auditEvents
        ];
        return workspace;
      }

      const selectedTrucks = trucks.length > 0 ? trucks : [];
      const events = selectedTrucks.slice(0, 3).map((truck, index): TelematicsEvent => ({
        id: createId('telemetry'),
        provider: connectorId,
        truckId: truck.id,
        truckLabel: getTruckLabel(truck),
        recordedAt: new Date(Date.now() - index * 3600 * 1000),
        odometer: truck.mileage + 120 + index * 18,
        fuelPercent: Math.max(18, 76 - index * 22),
        miles: 118 + index * 34,
        fuelGallons: 14 + index * 3,
        faultCode: index === 1 ? 'SPN 520211/FMI 4' : undefined,
        severity: index === 1 ? 'high' : 'low',
        description: index === 1 ? 'Aftertreatment sensor voltage low' : 'Trip telemetry imported'
      }));

      workspace.telematicsEvents = [...events, ...workspace.telematicsEvents].slice(0, 100);
      workspace.connectors = workspace.connectors.map(connector =>
        connector.id === connectorId
          ? {
              ...connector,
              status: 'connected',
              lastSync: new Date(),
              syncHealth: connectorId === 'GPS/ELD CSV' ? 92 : 96,
              recordsSynced: connector.recordsSynced + events.length
            }
          : connector
      );
      workspace.auditEvents = [
        this.audit(actor, 'Synced telematics', 'Telematics', `${events.length} records imported from ${connectorId}`),
        ...workspace.auditEvents
      ];

      return workspace;
    });
  }

  public importTelematicsCsv(userId: string, csvText: string, trucks: Truck[], actor: string): FleetOpsWorkspace {
    const rows = this.parseCsv(csvText).filter(row => row.some(value => value.trim()));
    const [headerRow, ...dataRows] = rows;
    const headers = headerRow ? headerRow.map(header => this.normalizeCsvHeader(header)) : [];

    return this.updateWorkspace(userId, workspace => {
      const imported = dataRows.map((row): TelematicsEvent | null => {
        const record = headers.reduce<Record<string, string>>((acc, header, index) => {
          acc[header] = row[index]?.trim() || '';
          return acc;
        }, {});
        const truck = this.resolveTruck(record, trucks);
        if (!truck) return null;

        return {
          id: createId('telemetry'),
          provider: 'GPS/ELD CSV' as IntegrationProvider,
          truckId: truck.id,
          truckLabel: getTruckLabel(truck),
          recordedAt: this.safeDate(record.recordedAt),
          odometer: this.safeNumber(record.odometer),
          fuelPercent: this.safeNumber(record.fuelPercent),
          fuelGallons: this.safeNumber(record.fuelGallons),
          miles: this.safeNumber(record.miles),
          faultCode: record.faultCode || undefined,
          severity: this.normalizeSeverity(record.severity),
          description: record.description || 'Imported GPS/ELD row'
        };
      }).filter((event): event is TelematicsEvent => Boolean(event));

      workspace.telematicsEvents = [...imported, ...workspace.telematicsEvents].slice(0, 100);
      workspace.connectors = workspace.connectors.map(connector =>
        connector.id === 'GPS/ELD CSV'
          ? { ...connector, status: 'connected', lastSync: new Date(), syncHealth: 90, recordsSynced: connector.recordsSynced + imported.length }
          : connector
      );
      workspace.auditEvents = [
        this.audit(actor, 'Imported ELD file', 'Telematics', `${imported.length} rows imported from CSV`),
        ...workspace.auditEvents
      ];

      return workspace;
    });
  }

  public addTeamMember(
    userId: string,
    member: Omit<TeamMember, 'id' | 'status' | 'inviteCode' | 'inviteLink' | 'inviteSentAt' | 'inviteExpiresAt' | 'acceptedAt' | 'createdAt' | 'updatedAt'>,
    actor: string
  ): FleetOpsWorkspace {
    return this.updateWorkspace(userId, workspace => {
      const email = member.email.trim().toLowerCase();
      const existing = workspace.teamMembers.find(item => item.email.toLowerCase() === email && item.status !== 'disabled');
      if (existing) {
        workspace.auditEvents = [
          this.audit(actor, 'Invite skipped', 'Company account', `${email} is already on this team`),
          ...workspace.auditEvents
        ];
        return workspace;
      }

      const inviteCode = createId('TEAM').replace(/-/g, '').slice(0, 18).toUpperCase();
      const inviteSentAt = new Date();
      const inviteExpiresAt = new Date(Date.now() + INVITE_LIFETIME_DAYS * 86400000);
      const memberId = createId('member');
      const newMember: TeamMember = {
        ...member,
        email,
        id: memberId,
        status: 'pending',
        inviteCode,
        inviteLink: this.buildInviteLink({
          code: inviteCode,
          workspaceUserId: userId,
          memberId,
          email,
          role: member.role,
          permissions: member.permissions,
          status: 'pending',
          expiresAt: inviteExpiresAt.toISOString(),
          createdAt: inviteSentAt.toISOString()
        }),
        inviteSentAt,
        inviteExpiresAt,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      workspace.teamMembers = [newMember, ...workspace.teamMembers];
      this.upsertInviteIndex(userId, newMember, 'pending');
      workspace.auditEvents = [
        this.audit(actor, 'Invite pending', 'Company account', `${newMember.name} was emailed an invite as ${newMember.role}`),
        ...workspace.auditEvents
      ];

      return workspace;
    });
  }

  public updateTeamMember(userId: string, memberId: string, updates: Partial<TeamMember>, actor: string): FleetOpsWorkspace {
    return this.updateWorkspace(userId, workspace => {
      const existing = workspace.teamMembers.find(member => member.id === memberId);
      if (!existing) return workspace;

      workspace.teamMembers = workspace.teamMembers.map(member =>
        member.id === memberId ? { ...member, ...updates, updatedAt: new Date() } : member
      );
      const member = workspace.teamMembers.find(item => item.id === memberId);
      if (member) this.upsertInviteIndex(userId, member, member.status);
      workspace.auditEvents = [
        this.audit(actor, 'Updated user access', 'Company account', `${member?.name || memberId} permissions changed`),
        ...workspace.auditEvents
      ];

      return workspace;
    });
  }

  public resendTeamInvite(userId: string, memberId: string, actor: string): FleetOpsWorkspace {
    return this.updateWorkspace(userId, workspace => {
      const member = workspace.teamMembers.find(item => item.id === memberId);
      if (!member || !this.isPendingInvite(member.status)) return workspace;

      const inviteCode = createId('TEAM').replace(/-/g, '').slice(0, 18).toUpperCase();
      const inviteSentAt = new Date();
      const inviteExpiresAt = new Date(Date.now() + INVITE_LIFETIME_DAYS * 86400000);

      this.revokeInviteCode(member.inviteCode);
      workspace.teamMembers = workspace.teamMembers.map(item =>
        item.id === memberId
          ? {
              ...item,
              inviteCode,
              inviteLink: this.buildInviteLink({
                code: inviteCode,
                workspaceUserId: userId,
                memberId: item.id,
                email: item.email,
                role: item.role,
                permissions: item.permissions,
                status: 'pending',
                expiresAt: inviteExpiresAt.toISOString(),
                createdAt: inviteSentAt.toISOString()
              }),
              inviteSentAt,
              inviteExpiresAt,
              status: 'pending',
              updatedAt: new Date()
            }
          : item
      );
      const updated = workspace.teamMembers.find(item => item.id === memberId);
      if (updated) this.upsertInviteIndex(userId, updated, 'pending');
      workspace.auditEvents = [
        this.audit(actor, 'Resent invite', 'Company account', `${member.email} received a refreshed invite`),
        ...workspace.auditEvents
      ];

      return workspace;
    });
  }

  public revokeTeamInvite(userId: string, memberId: string, actor: string): FleetOpsWorkspace {
    return this.updateWorkspace(userId, workspace => {
      const member = workspace.teamMembers.find(item => item.id === memberId);
      if (!member) return workspace;

      this.revokeInviteCode(member.inviteCode);
      workspace.teamMembers = workspace.teamMembers.map(item =>
        item.id === memberId ? { ...item, status: 'disabled', updatedAt: new Date() } : item
      );
      workspace.auditEvents = [
        this.audit(actor, 'Revoked access', 'Company account', `${member.email} invite or access was disabled`),
        ...workspace.auditEvents
      ];

      return workspace;
    });
  }

  public markInviteAccepted(workspaceUserId: string, memberId: string, actor: string): FleetOpsWorkspace {
    return this.updateWorkspace(workspaceUserId, workspace => {
      const member = workspace.teamMembers.find(item => item.id === memberId);
      if (!member) return workspace;

      workspace.teamMembers = workspace.teamMembers.map(item =>
        item.id === memberId
          ? { ...item, status: 'active', acceptedAt: new Date(), lastActive: new Date(), updatedAt: new Date() }
          : item
      );
      const updated = workspace.teamMembers.find(item => item.id === memberId);
      if (updated) this.upsertInviteIndex(workspaceUserId, updated, 'active');
      workspace.auditEvents = [
        this.audit(actor, 'Accepted invite', 'Company account', `${member.email} joined the workspace`),
        ...workspace.auditEvents
      ];

      return workspace;
    });
  }

  public getInviteEmail(member: TeamMember, companyName = 'FleetFix'): { subject: string; body: string; mailtoHref: string } {
    const workspaceName = companyName.trim().toLowerCase() === 'fleetfix' ? 'your fleet' : companyName;
    const subject = `You're invited to join ${workspaceName} in FleetFix`;
    const body = [
      `Hi ${member.name},`,
      '',
      `${workspaceName} invited you to join its FleetFix workspace as ${member.role}.`,
      `Invite code: ${member.inviteCode}`,
      `Invite link: ${member.inviteLink}`,
      '',
      'Use this invite to create your account and join the company workspace.',
      `This invite expires on ${member.inviteExpiresAt.toLocaleDateString()}.`
    ].join('\n');

    return {
      subject,
      body,
      mailtoHref: `mailto:${encodeURIComponent(member.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    };
  }

  public consumeInviteIfValid(code: string, email: string, actor: string, token?: string): TeamInviteIndexEntry | null {
    const normalizedCode = code.trim().toUpperCase();
    const normalizedEmail = email.trim().toLowerCase();
    const invites = this.getInviteIndex();
    const invite = invites[normalizedCode];
    const tokenInvite = this.getTokenInvite(token, normalizedCode, normalizedEmail);
    if (!invite || invite.email !== normalizedEmail) {
      if (tokenInvite) this.savePendingTeamClaim(tokenInvite);
      return tokenInvite;
    }

    const isExpired = new Date(invite.expiresAt).getTime() < Date.now();
    if (isExpired || !this.isPendingInvite(invite.status)) {
      invites[normalizedCode] = { ...invite, status: isExpired ? 'expired' : invite.status };
      this.saveInviteIndex(invites);
      return null;
    }

    void this.acceptRemoteTeamInvite(invite, actor);
    return invite;
  }

  public validateTeamInvite(code: string, email: string, token?: string): TeamInviteIndexEntry | null {
    const normalizedCode = code.trim().toUpperCase();
    const normalizedEmail = email.trim().toLowerCase();
    const invite = this.getInviteIndex()[normalizedCode];
    const tokenInvite = this.getTokenInvite(token, normalizedCode, normalizedEmail);
    if (!invite || invite.email !== normalizedEmail) return tokenInvite;

    const isExpired = new Date(invite.expiresAt).getTime() < Date.now();
    if (isExpired || !this.isPendingInvite(invite.status)) return null;

    return invite;
  }

  public readTeamInviteToken(token?: string): TeamInviteIndexEntry | null {
    return this.getTokenInvite(token);
  }

  public savePendingTeamClaim(invite: TeamInviteIndexEntry): void {
    localStorage.setItem(`fleetfix_team_claim_${invite.email.toLowerCase()}`, JSON.stringify({
      workspaceUserId: invite.workspaceUserId,
      memberId: invite.memberId,
      role: invite.role,
      permissions: invite.permissions
    }));
  }

  public syncOfflineQueue(userId: string, actor: string): FleetOpsWorkspace {
    return this.updateWorkspace(userId, workspace => {
      const syncedCount = workspace.offlineQueue.length;
      workspace.inspections = workspace.inspections.map(inspection => ({ ...inspection, synced: true, updatedAt: new Date() }));
      workspace.offlineQueue = [];
      workspace.auditEvents = [
        this.audit(actor, 'Synced offline queue', 'Mobile workflow', `${syncedCount} offline records synced`),
        ...workspace.auditEvents
      ];

      return workspace;
    });
  }

  public calculatePredictiveScores(trucks: Truck[], maintenance: MaintenanceEntry[], workspace: FleetOpsWorkspace): PredictiveMaintenanceScore[] {
    return trucks.map(truck => {
      const records = maintenance.filter(record => record.truckId === truck.id);
      const latestService = records
        .map(record => toDate(record.date))
        .sort((a, b) => b.getTime() - a.getTime())[0];
      const daysSinceService = latestService
        ? Math.floor((Date.now() - latestService.getTime()) / 86400000)
        : 365;
      const emergencyRepairs = records.filter(record => record.type === 'Emergency Repair' || record.type === 'General Repair').length;
      const openIssues = workspace.issueReports.filter(issue => issue.truckId === truck.id && issue.status !== 'resolved');
      const openWorkOrders = workspace.workOrders.filter(order => order.truckId === truck.id && order.status !== 'completed' && order.status !== 'invoiced');
      const faults = workspace.telematicsEvents.filter(event => event.truckId === truck.id && event.faultCode);

      let riskScore = 12;
      const drivers: string[] = [];
      const recommendedActions: string[] = [];

      if (truck.status === 'Out for Repair') {
        riskScore += 30;
        drivers.push('Vehicle is currently out for repair');
        recommendedActions.push('Review active work orders and parts availability');
      } else if (truck.status === 'Needs Attention') {
        riskScore += 22;
        drivers.push('Vehicle is marked needs attention');
        recommendedActions.push('Schedule triage inspection');
      }

      if (truck.mileage > 300000) {
        riskScore += 18;
        drivers.push('Mileage is above 300k');
        recommendedActions.push('Increase preventive maintenance frequency');
      } else if (truck.mileage > 220000) {
        riskScore += 10;
        drivers.push('Mileage is entering higher-risk range');
      }

      if (daysSinceService > 120) {
        riskScore += 18;
        drivers.push(`${daysSinceService} days since last recorded service`);
        recommendedActions.push('Create preventive service work order');
      } else if (daysSinceService > 75) {
        riskScore += 8;
        drivers.push(`${daysSinceService} days since last service`);
      }

      if (emergencyRepairs > 0) {
        riskScore += emergencyRepairs * 8;
        drivers.push(`${emergencyRepairs} recent reactive repair${emergencyRepairs > 1 ? 's' : ''}`);
      }

      if (openIssues.length > 0) {
        riskScore += openIssues.length * 7;
        drivers.push(`${openIssues.length} open driver/mechanic issue${openIssues.length > 1 ? 's' : ''}`);
        recommendedActions.push('Resolve open mobile issue reports');
      }

      if (openWorkOrders.length > 0) {
        riskScore += openWorkOrders.length * 6;
        drivers.push(`${openWorkOrders.length} active work order${openWorkOrders.length > 1 ? 's' : ''}`);
      }

      if (faults.length > 0) {
        riskScore += faults.some(fault => fault.severity === 'critical' || fault.severity === 'high') ? 18 : 8;
        drivers.push(`${faults.length} telematics fault code${faults.length > 1 ? 's' : ''}`);
        recommendedActions.push('Pull diagnostics from connected telematics provider');
      }

      const boundedScore = Math.min(100, Math.round(riskScore));
      const riskLevel: PredictiveMaintenanceScore['riskLevel'] = boundedScore >= 80
        ? 'critical'
        : boundedScore >= 60
          ? 'high'
          : boundedScore >= 35
            ? 'moderate'
            : 'low';

      return {
        truckId: truck.id,
        truckLabel: getTruckLabel(truck),
        riskScore: boundedScore,
        riskLevel,
        drivers: drivers.length ? drivers : ['No active fault signals found'],
        recommendedActions: recommendedActions.length ? recommendedActions : ['Keep current preventive maintenance plan'],
        nextServiceWindow: boundedScore >= 60 ? 'Within 7 days' : boundedScore >= 35 ? 'Within 30 days' : 'Normal interval'
      };
    }).sort((a, b) => b.riskScore - a.riskScore);
  }

  public calculateReports(trucks: Truck[], maintenance: MaintenanceEntry[], workspace: FleetOpsWorkspace): FleetReportMetrics {
    const totalMileage = trucks.reduce((sum, truck) => sum + Math.max(truck.mileage, 1), 0);
    const workOrderCost = workspace.workOrders.reduce((sum, order) => {
      const labor = order.laborHours * order.laborRate;
      const parts = order.parts.reduce((partSum, part) => partSum + part.quantity * part.unitCost, 0);
      return sum + labor + parts;
    }, 0);
    const maintenanceCost = maintenance.reduce((sum, record) => sum + (record.cost || 0), 0) + workOrderCost;
    const preventiveRecords = maintenance.filter(record =>
      ['Preventive Maintenance', 'DOT Inspection', 'Annual Inspection', 'Safety Check', 'Oil Change'].includes(record.type)
    ).length;
    const pmCompliance = maintenance.length > 0 ? Math.round((preventiveRecords / maintenance.length) * 100) : 100;
    const utilization = trucks.length > 0 ? Math.round((trucks.filter(truck => truck.status === 'In Service').length / trucks.length) * 100) : 0;
    const fuelGallons = workspace.telematicsEvents.reduce((sum, event) => sum + (event.fuelGallons || 0), 0);
    const downtimeHours = workspace.workOrders
      .filter(order => ['in-progress', 'awaiting-approval', 'approved'].includes(order.status))
      .reduce((sum, order) => sum + Math.max(order.laborHours, 2), 0);
    const openWorkOrders = workspace.workOrders.filter(order => !['completed', 'invoiced'].includes(order.status)).length;
    const overdueCompliance = workspace.complianceDocuments.filter(doc => doc.status === 'expired').length;
    const criticalFaults = workspace.telematicsEvents.filter(event => event.faultCode && ['high', 'critical'].includes(event.severity)).length;

    const monthlyCosts = maintenance.reduce<Record<string, number>>((acc, record) => {
      const date = toDate(record.date);
      const key = date.toLocaleString('default', { month: 'short', year: '2-digit' });
      acc[key] = (acc[key] || 0) + (record.cost || 0);
      return acc;
    }, {});

    return {
      costPerMile: totalMileage > 0 ? maintenanceCost / totalMileage : 0,
      totalMaintenanceCost: maintenanceCost,
      downtimeHours,
      pmCompliance,
      utilization,
      fuelGallons,
      fuelCostEstimate: fuelGallons * FUEL_COST_PER_GALLON,
      openWorkOrders,
      overdueCompliance,
      criticalFaults,
      monthlyCosts: Object.entries(monthlyCosts).map(([month, cost]) => ({ month, cost }))
    };
  }

  public exportReportCsv(trucks: Truck[], maintenance: MaintenanceEntry[], workspace: FleetOpsWorkspace): string {
    const reports = this.calculateReports(trucks, maintenance, workspace);
    const distanceUnit = getSavedDistanceUnit();
    const costPerDistance = distanceUnit === 'km' ? reports.costPerMile / KM_PER_MILE : reports.costPerMile;

    return [
      'metric,value',
      `Cost per ${getDistanceUnitLabel(distanceUnit)},${costPerDistance.toFixed(4)}`,
      `Total maintenance cost,${reports.totalMaintenanceCost.toFixed(2)}`,
      `Downtime hours,${reports.downtimeHours.toFixed(1)}`,
      `PM compliance,${reports.pmCompliance}%`,
      `Utilization,${reports.utilization}%`,
      `Fuel gallons,${reports.fuelGallons.toFixed(1)}`,
      `Estimated fuel cost,${reports.fuelCostEstimate.toFixed(2)}`,
      `Open work orders,${reports.openWorkOrders}`,
      `Expired compliance records,${reports.overdueCompliance}`,
      `High-critical fault codes,${reports.criticalFaults}`
    ].join('\n');
  }

  private updateWorkspace(userId: string, updater: (workspace: FleetOpsWorkspace) => FleetOpsWorkspace): FleetOpsWorkspace {
    const workspace = this.getWorkspace(userId);
    const updated = updater({ ...workspace });
    this.saveWorkspace(userId, updated);
    return updated;
  }

  private getStorageKey(userId: string): string {
    return `${STORAGE_PREFIX}${userId}`;
  }

  private getComplianceStatus(expirationDate: Date): ComplianceDocument['status'] {
    const daysUntilExpiration = Math.ceil((expirationDate.getTime() - Date.now()) / 86400000);
    if (daysUntilExpiration < 0) return 'expired';
    if (daysUntilExpiration <= 30) return 'due-soon';
    return 'valid';
  }

  private normalizeSeverity(value: string): Severity {
    if (value === 'critical' || value === 'high' || value === 'medium' || value === 'low') {
      return value;
    }

    return 'low';
  }

  private normalizeCsvHeader(header: string): string {
    const normalized = header.trim().toLowerCase().replace(/[\s_-]+/g, '');
    const aliases: Record<string, string> = {
      truckid: 'truckId',
      vehicleid: 'truckId',
      vehicle: 'truckId',
      unit: 'unit',
      unitnumber: 'unit',
      vin: 'vin',
      plate: 'licensePlate',
      licenseplate: 'licensePlate',
      timestamp: 'recordedAt',
      recordedat: 'recordedAt',
      datetime: 'recordedAt',
      date: 'recordedAt',
      odometer: 'odometer',
      mileage: 'odometer',
      miles: 'miles',
      tripmiles: 'miles',
      fuel: 'fuelGallons',
      fuelgallons: 'fuelGallons',
      fuelpercent: 'fuelPercent',
      fuelpercentage: 'fuelPercent',
      fault: 'faultCode',
      faultcode: 'faultCode',
      dtc: 'faultCode',
      spn: 'faultCode',
      severity: 'severity',
      level: 'severity',
      description: 'description',
      eventdescription: 'description',
      notes: 'description'
    };

    return aliases[normalized] || header.trim();
  }

  private resolveTruck(record: Record<string, string>, trucks: Truck[]): Truck | undefined {
    const truckReference = [record.truckId, record.vin, record.licensePlate, record.unit]
      .filter(Boolean)
      .map(value => value.trim().toLowerCase());

    return trucks.find(truck => {
      const candidates = [truck.id, truck.vin, truck.licensePlate, truck.nickname]
        .filter(Boolean)
        .map(value => String(value).trim().toLowerCase());
      return candidates.some(candidate => truckReference.includes(candidate));
    });
  }

  private safeNumber(value?: string): number | undefined {
    if (!value) return undefined;
    const parsed = Number(value.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private safeDate(value?: string): Date {
    if (!value) return new Date();
    if (/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
      return new Date(`${value.trim()}T12:00:00`);
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }

  private buildInviteLink(invite: TeamInviteTokenPayload): string {
    const basePath = `${this.getPublicAppOrigin()}/signup`;
    const token = this.encodeInviteToken(invite);
    return `${basePath}?invite=${encodeURIComponent(invite.code)}&email=${encodeURIComponent(invite.email)}&token=${encodeURIComponent(token)}`;
  }

  private getPublicAppOrigin(): string {
    return PUBLIC_APP_URL.replace(/\/+$/, '');
  }

  private encodeInviteToken(invite: TeamInviteTokenPayload): string {
    const json = JSON.stringify(invite);
    const bytes = new TextEncoder().encode(json);
    const binary = Array.from(bytes, byte => String.fromCharCode(byte)).join('');
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  private decodeInviteToken(token?: string): TeamInviteTokenPayload | null {
    if (!token) return null;
    try {
      const normalized = token.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
      const binary = atob(padded);
      const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
      return JSON.parse(new TextDecoder().decode(bytes));
    } catch (error) {
      console.error('Error decoding team invite token:', error);
      return null;
    }
  }

  private getTokenInvite(token?: string, code?: string, email?: string): TeamInviteIndexEntry | null {
    const payload = this.decodeInviteToken(token);
    if (!payload) return null;

    const normalizedCode = code?.trim().toUpperCase();
    const normalizedEmail = email?.trim().toLowerCase();
    if (normalizedCode && payload.code !== normalizedCode) return null;
    if (normalizedEmail && payload.email.toLowerCase() !== normalizedEmail) return null;

    const isExpired = new Date(payload.expiresAt).getTime() < Date.now();
    if (isExpired || !this.isPendingInvite(payload.status)) return null;

    return {
      code: payload.code,
      workspaceUserId: payload.workspaceUserId,
      memberId: payload.memberId,
      companyId: payload.workspaceUserId,
      email: payload.email.toLowerCase(),
      role: payload.role,
      permissions: payload.permissions,
      status: payload.status,
      expiresAt: payload.expiresAt,
      createdAt: payload.createdAt
    };
  }

  private getInviteIndex(): Record<string, TeamInviteIndexEntry> {
    try {
      return JSON.parse(localStorage.getItem(TEAM_INVITE_INDEX_KEY) || '{}');
    } catch (error) {
      console.error('Error loading team invite index:', error);
      return {};
    }
  }

  private saveInviteIndex(invites: Record<string, TeamInviteIndexEntry>): void {
    localStorage.setItem(TEAM_INVITE_INDEX_KEY, JSON.stringify(invites));
  }

  private upsertInviteIndex(userId: string, member: TeamMember, status: TeamInviteIndexEntry['status']): void {
    const invites = this.getInviteIndex();
    invites[member.inviteCode] = {
      code: member.inviteCode,
      workspaceUserId: userId,
      memberId: member.id,
      companyId: userId,
      email: member.email.toLowerCase(),
      role: member.role,
      permissions: member.permissions,
      status,
      expiresAt: member.inviteExpiresAt.toISOString(),
      createdAt: member.inviteSentAt.toISOString()
    };
    this.saveInviteIndex(invites);
  }

  private revokeInviteCode(code?: string): void {
    if (!code) return;
    const invites = this.getInviteIndex();
    if (invites[code]) {
      invites[code] = { ...invites[code], status: 'revoked' };
      this.saveInviteIndex(invites);
    }
  }

  private isPendingInvite(status: TeamInviteIndexEntry['status'] | TeamMember['status']): boolean {
    return status === 'pending' || status === 'invited';
  }

  private parseCsv(csvText: string): string[][] {
    const rows: string[][] = [];
    let current = '';
    let row: string[] = [];
    let inQuotes = false;

    for (let index = 0; index < csvText.length; index += 1) {
      const char = csvText[index];
      const next = csvText[index + 1];

      if (char === '"' && inQuotes && next === '"') {
        current += '"';
        index += 1;
      } else if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        row.push(current);
        current = '';
      } else if ((char === '\n' || char === '\r') && !inQuotes) {
        if (char === '\r' && next === '\n') index += 1;
        row.push(current);
        rows.push(row);
        row = [];
        current = '';
      } else {
        current += char;
      }
    }

    row.push(current);
    rows.push(row);
    return rows;
  }

  private audit(actor: string, action: string, entity: string, detail: string): FleetAuditEvent {
    return {
      id: createId('audit'),
      actor,
      action,
      entity,
      detail,
      createdAt: new Date()
    };
  }

  private createSeedWorkspace(): FleetOpsWorkspace {
    return {
      inspections: [],
      issueReports: [],
      workOrders: [],
      connectors: [
        { id: 'Samsara', name: 'Samsara', status: 'not-connected', authMode: 'api-key', syncHealth: 0, recordsSynced: 0, notes: 'API token-ready connector for vehicles, diagnostics, and safety events.' },
        { id: 'Motive', name: 'Motive', status: 'not-connected', authMode: 'api-key', syncHealth: 0, recordsSynced: 0, notes: 'ELD, fuel, driver, and vehicle event import path.' },
        { id: 'Geotab', name: 'Geotab', status: 'not-connected', authMode: 'oauth', syncHealth: 0, recordsSynced: 0, notes: 'OAuth-ready connector for MyGeotab trips, odometer, and engine faults.' },
        { id: 'GPS/ELD CSV', name: 'GPS/ELD CSV', status: 'not-connected', authMode: 'file-import', syncHealth: 0, recordsSynced: 0, notes: 'Upload CSV exports from GPS or ELD vendors.' }
      ],
      telematicsEvents: [],
      complianceDocuments: [],
      teamMembers: [],
      auditEvents: [],
      offlineQueue: []
    };
  }

  private reviveWorkspace(raw: FleetOpsWorkspace, userId: string): FleetOpsWorkspace {
    return {
      inspections: (raw.inspections || []).filter(item => !isSeedRecord(item)).map(item => ({ ...item, createdAt: toDate(item.createdAt), updatedAt: toDate(item.updatedAt) })),
      issueReports: (raw.issueReports || []).filter(item => !isSeedRecord(item)).map(item => ({ ...item, createdAt: toDate(item.createdAt), updatedAt: toDate(item.updatedAt) })),
      workOrders: (raw.workOrders || []).filter(item => !isSeedRecord(item)).map(item => ({
        ...item,
        scheduledDate: toDate(item.scheduledDate),
        approvedAt: item.approvedAt ? toDate(item.approvedAt) : undefined,
        createdAt: toDate(item.createdAt),
        updatedAt: toDate(item.updatedAt)
      })),
      connectors: (raw.connectors || this.createSeedWorkspace().connectors).map(item => ({
        ...item,
        lastSync: item.lastSync ? toDate(item.lastSync) : undefined
      })),
      telematicsEvents: (raw.telematicsEvents || []).filter(item => !isSeedRecord(item)).map(item => ({ ...item, recordedAt: toDate(item.recordedAt) })),
      complianceDocuments: (raw.complianceDocuments || []).filter(item => !isSeedRecord(item)).map(item => ({
        ...item,
        expirationDate: toDate(item.expirationDate),
        status: this.getComplianceStatus(toDate(item.expirationDate)),
        createdAt: toDate(item.createdAt),
        updatedAt: toDate(item.updatedAt)
      })),
      teamMembers: (raw.teamMembers || []).filter(item => !isSeedRecord(item)).map(item => {
        const inviteCode = item.inviteCode || createId('TEAM').replace(/-/g, '').slice(0, 18).toUpperCase();
        const status = item.status === 'invited' ? 'pending' : item.status;
        const inviteSentAt = item.inviteSentAt ? toDate(item.inviteSentAt) : toDate(item.createdAt);
        const inviteExpiresAt = item.inviteExpiresAt ? toDate(item.inviteExpiresAt) : new Date(Date.now() + INVITE_LIFETIME_DAYS * 86400000);
        return {
          ...item,
          status,
          inviteCode,
          inviteLink: this.buildInviteLink({
            code: inviteCode,
            workspaceUserId: userId,
            memberId: item.id,
            email: item.email,
            role: item.role,
            permissions: item.permissions,
            status,
            expiresAt: inviteExpiresAt.toISOString(),
            createdAt: inviteSentAt.toISOString()
          }),
          inviteSentAt,
          inviteExpiresAt,
          acceptedAt: item.acceptedAt ? toDate(item.acceptedAt) : undefined,
          lastActive: item.lastActive ? toDate(item.lastActive) : undefined,
          createdAt: toDate(item.createdAt),
          updatedAt: toDate(item.updatedAt)
        };
      }),
      auditEvents: (raw.auditEvents || []).filter(item => !isSeedRecord(item)).map(item => ({ ...item, createdAt: toDate(item.createdAt) })),
      offlineQueue: (raw.offlineQueue || []).map(item => ({ ...item, createdAt: toDate(item.createdAt) }))
    };
  }
}

export const fleetOpsService = new FleetOpsService();
